import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { parseISO } from 'date-fns';
import { Shipments } from 'src/app/models/shipment';
import { OrdersService } from 'src/app/services/orders.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';
import {
  SortDirection,
  nextSortState,
  sortItems,
} from 'src/app/utils/table-sort';

/**
 * StatusSpedizioniComponent — lista status spedizioni (route
 * `/status-spedizioni`). Una spedizione ha più stati (in transito,
 * consegnata, ritardata, errore) tracciati lato corriere.
 *
 * Sub-modali aperti da row actions:
 *   • app-status-spedizioni-dettaggli — dettagli spedizione (info
 *     card + timeline)
 *   • app-accesso-funzionari-tracking — modale tracking esterna
 *     (solo se `os_tracking_number` è valorizzato)
 *
 * Phase 1.5 / v2.8.0 — TS cleaned up (Chunk F.1):
 *   • dropped kendo `GridComponent` ViewChild + `PageChangeEvent`
 *     + `loadProducts`/`pageChange` (no more kendo-grid),
 *   • dropped custom `deepCopy` (non era nemmeno usato),
 *   • dropped ~25 console.log,
 *   • dropped ~25 dead state fields: `shipments` (alias di
 *     gridData), `filteredShipments` (filter ora client-side),
 *     `isSearched`, `gridView`, `data`, `customers`, `dates`,
 *     `order_numbers`, `customerIndex`, `carrierIndex`, `filterzm`,
 *     `min`, `max`, `yearRange`, `searchString` labels,
 *     `carrierString`, `searchDate`, `searchOrderNumber`,
 *     `showAll2/3`, `pageSize`, `skip`, `selectedMonth`, `customer`,
 *     `date`, `order_number`,
 *   • dropped 4 dead handlers (`onMessengerNameSelect`,
 *     `onCarrierSelect`, `onOrderNumber`, `onDateSelect`) —
 *     sostituiti da binding diretto in template,
 *   • dropped `filterList`, `clearList`, `doRefresh` (legacy ionic),
 *   • dropped `onMonthSelect` (era solo console.log),
 *   • dropped `monthNames` duplicato dentro a `getFormattedMonth`
 *     (DRY: usa la lista a livello classe),
 *   • dropped `subscriber: Subscription[]` + ngOnDestroy unsubscribe
 *     (sostituito da takeUntilDestroyed),
 *   • switch to inject() + takeUntilDestroyed.
 *
 * Behaviour preserved 1:1: loadShipments con sort by shipment_date
 * desc, filterByMonth (chip mese), goToDetails / goToTracking
 * action openers, query-param init flow.
 */
@Component({
  selector: 'app-status-spedizioni',
  templateUrl: './status-spedizioni.component.html',
  styleUrls: ['./status-spedizioni.component.scss'],
  standalone: false,
})


export class StatusSpedizioniComponent implements OnInit {

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly orderService = inject(OrdersService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  public readonly closeServ = inject(OpenModalService);

  // ── Data ────────────────────────────────────────────────────────
  public gridData: Shipments.ShipmentList[] = [];
  private originalData: Shipments.ShipmentList[] = [];
  public carriers: string[] = [];

  // ── UI state ────────────────────────────────────────────────────
  public loading = false;
  public filterTerm = '';
  public chosenCarrier: string | null = null;
  public clickedId: string | number = '';
  public shipment_id: string | number = '';
  public selectedMonth: string | null = null;
  public readonly showAll = 'Mostra tutti';

  public sortColumn: string | null = null;
  public sortDirection: SortDirection = 'asc';

  // ── Date range filter (defaults: 2021-01-01 → today) ───────────
  startDate: Date = new Date(2021, 0, 1);
  endDate: Date = new Date();

  filters = {
    dateOrginizedBy: {
      text: 'Data ordine',
      value: 'or_order_number',
    },
    startingDate: this.startDate as Date | null,
    endDate: this.endDate as Date,
    minDate: new Date(),
    maxDate: new Date(),
  };

  /** Italian month names used by both the filter chip and the
   *  filterByMonth handler. Single source of truth (was duplicated
   *  in the legacy code). */
  public readonly monthNames: string[] = [
    'Gennaio',
    'Febbraio',
    'Marzo',
    'Aprile',
    'Maggio',
    'Giugno',
    'Luglio',
    'Agosto',
    'Settembre',
    'Ottobre',
    'Novembre',
    'Dicembre',
  ];

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loading = true;
    this.activatedRoute.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (Object.keys(params).length === 0) {
          this.getShipmentList();
        } else {
          const searchParam: string = params['search'] ?? '';
          const dateParam: string = params['data'];
          this.filters.startingDate = dateParam ? parseISO(dateParam) : null;
          this.getShipmentList(searchParam);
        }
        this.cdr.detectChanges();
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loader.
  // ──────────────────────────────────────────────────────────────────

  getShipmentList(searchString?: string): void {
    if (searchString !== undefined) this.filterTerm = searchString;
    this.loading = true;
    this.orderService
      .getShippmentsList(this.filterTerm)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const sorted = (result ?? []).sort(
            (a, b) =>
              new Date(b.shipment_date).getTime() -
              new Date(a.shipment_date).getTime(),
          );
          //this.originalData = sorted;
          this.gridData = sorted;
          this.rebuildCarriers(sorted);
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private rebuildCarriers(list: Shipments.ShipmentList[]): void {
    const set = new Set(list.map((s) => s.sm_carrier_desc ?? ''));
    this.carriers = [this.showAll, ...Array.from(set)];
  }

  // ──────────────────────────────────────────────────────────────────
  // Search + filters.
  // ──────────────────────────────────────────────────────────────────

  searchOrders(): void {
    this.getShipmentList();
  }

  clearSearch(): void {
    this.filterTerm = '';
    this.filters.startingDate = this.startDate;
    this.selectedMonth = null;
    this.chosenCarrier = null;
    this.getShipmentList();
  }

  onCarrierChange(carrier: string | null): void {
    this.chosenCarrier =
      !carrier || carrier === this.showAll ? null : carrier;
  }

  onMonthSelect(month: string | null): void {
    this.selectedMonth = month ?? null;
  }

  onStartDateChange(value: Date | string | null): void {
    this.filters.startingDate = !value
      ? null
      : value instanceof Date
        ? value
        : new Date(value);
  }

  onEndDateChange(value: Date | string | null): void {
    if (!value) return;
    this.filters.endDate = value instanceof Date ? value : new Date(value);
  }

  // ──────────────────────────────────────────────────────────────────
  // UI helpers.
  // ──────────────────────────────────────────────────────────────────

  /** Maps shipment status code to a visual state class. */
  statusState(item: Shipments.ShipmentList): string {
    if (!item.sm_status_cod || item.sm_status_cod === 0) return 'pending';
    // Convention used elsewhere in the app for shipment statuses.
    // Higher cod ≈ further along in the delivery cycle.
    if (item.sm_status_cod >= 100) return 'delivered';
    if (item.sm_status_cod >= 50) return 'in-transit';
    if (item.sm_status_cod >= 10) return 'shipped';
    return 'pending';
  }

  /** Returns true if the closing date is the backend sentinel
   *  meaning "no closing yet" (`0001-01-01T00:00:00`). */
  isClosingEmpty(value: string | null | undefined): boolean {
    return !value || String(value).startsWith('0001-01-01');
  }

  // ──────────────────────────────────────────────────────────────────
  // Detail navigation.
  // ──────────────────────────────────────────────────────────────────

  goToDetails(id: string | number): void {
    this.clickedId = id;
    this.closeServ.onId();
  }

  goToTracking(trackId: string | number): void {
    this.shipment_id = trackId;
    this.closeServ.onOrderTrack();
  }

  /** Closes the tracking modal-chrome wrapper (Chunk F.1 polish
   *  2026-06-15): the tracking widget is content-only post-v2.4.0
   *  and the parent owns the close gesture. */
  closeTracking(): void {
    this.closeServ.offOrderTrack();
  }

  get filteredOrders(): Shipments.ShipmentList[] {
      const customer = this.chosenCarrier;
      const start = this.filters.startingDate;
      const end = this.filters.endDate;
      let items = this.gridData.filter((o) => {
        if (customer && o.sm_carrier_desc !== customer) return false;
        if (start && o.shipment_date && new Date(o.shipment_date) < start) {
          return false;
        }
        if (end && o.shipment_date && new Date(o.shipment_date) > end) {
          return false;
        }
        return true;
      });
      if (this.sortColumn) {
        items = sortItems(items, this.sortColumn, this.sortDirection);
      }
      return items;
    }

  sortBy(column: string): void {
    const next = nextSortState(this.sortColumn, this.sortDirection, column);
    this.sortColumn = next.column;
    this.sortDirection = next.direction;
    this.cdr.detectChanges();
  }
  
}
